import { useEffect, useRef, type RefObject } from 'react';

// The one click-outside rule: a document-level mousedown that lands outside the
// element fires the callback (close the popover/dropdown). Inside clicks — and
// any click while the element isn't mounted — do nothing.

// The handler itself, factored out so it is unit-testable in the node test
// environment (no DOM): the hook is just registration glue around it.
export function makeClickOutsideHandler(
  getEl: () => HTMLElement | null,
  onOutside: () => void,
): (e: { target: unknown }) => void {
  return (e) => {
    const el = getEl();
    if (el && !el.contains(e.target as Node)) onOutside();
  };
}

/**
 * Returns a ref to attach to the popover/dropdown root. A mousedown anywhere
 * outside that element invokes `onOutside`. The callback is read through a ref,
 * so an inline closure doesn't re-register the listener every render.
 */
export function useClickOutside<T extends HTMLElement>(onOutside: () => void): RefObject<T> {
  const ref = useRef<T>(null);
  const cb = useRef(onOutside);
  cb.current = onOutside;
  useEffect(() => {
    const onDown = makeClickOutsideHandler(
      () => ref.current,
      () => cb.current(),
    ) as (e: MouseEvent) => void;
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);
  return ref;
}
